/**
 * ═══════════════════════════════════════════════════════════════════════════
 * NEW PRODUCTS PAGE - VERSION 5.2 DYNAMIC
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * LOGIQUE:
 *   1. Lire infoSource.allSettings.pages.newProducts → pageSettings
 *   2. Résolution — CASCADE STRICTE à 3 niveaux :
 *
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │  NIVEAU 1 — pageSettings.sections[]  (DB AnimationSettings)     │
 *      │  NIVEAU 2 — pageSettings.facets[]    (DB AnimationSettings)     │
 *      │  NIVEAU 3 — PAGES_CONFIG_.newProducts (statique pagesConfig.js) │
 *      └─────────────────────────────────────────────────────────────────┘
 *
 *   SECTIONS INDÉPENDANTES (DataLoader propre):
 *     productGrid → facetKey: '__PRODUCTS_BASE__' → dataKey: 'products'
 *     → skip/limit gérés par SectionDataLoader dans DynamicPageRenderer
 *     → PAS de paginationProps global nécessaire
 *
 *   SECTIONS STANDARD (pageData global):
 *     userView, followMe, bestSellers, ...
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Layout from '../Layout/Layout';
import LoadingSwiper from '../slider/loadindSwiper';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import {
    PAGES_CONFIG_,
    SECTIONS_LIBRARY,
} from '../pageInside/exeemple_page/pagesConfig';
import { prices } from '../pageInside/fixedPrices';
import { isAuthenticated } from '../auth';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES MODULE
// ═══════════════════════════════════════════════════════════════════════════

const PAGE_ID = 'newProducts';

// ═══════════════════════════════════════════════════════════════════════════
// NIVEAU 3 — defaults depuis PAGES_CONFIG_.newProducts
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return ['productGrid', 'userView', 'followMe', 'bestSellers'];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const DEFAULT_CONFIG_OVERRIDES = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) return {};

    const result = {};
    config.sections.forEach(s => {
        if (!s.sectionId) return;
        const entry = {};
        const lib   = SECTIONS_LIBRARY[s.sectionId];

        if (lib?.defaultProps || s.props) {
            entry.props = { ...(lib?.defaultProps || {}), ...(s.props || {}) };
        }
        if (s.overrides && Object.keys(s.overrides).length > 0) {
            entry.overrides = { ...s.overrides };
        }
        if (Object.keys(entry).length > 0) result[s.sectionId] = entry;
    });
    return result;
})();

// ═══════════════════════════════════════════════════════════════════════════
// _extractFromSettings — NIVEAUX 1 & 2
// ═══════════════════════════════════════════════════════════════════════════

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;

    const { sections, facets } = settings;

    // ── Priorité 1 : sections[] ─────────────────────────────────────────────
    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);

        const configOverrides = {};
        sorted.forEach(s => {
            if (!s.sectionId) return;
            const entry = {};
            if (s.props     && Object.keys(s.props).length > 0)     entry.props     = s.props;
            if (s.overrides && Object.keys(s.overrides).length > 0) entry.overrides = s.overrides;
            if (Object.keys(entry).length > 0) configOverrides[s.sectionId] = entry;
        });

        if (resolvedFacets.length > 0) {
            return { facets: resolvedFacets, configOverrides };
        }
    }

    // ── Priorité 2 : facets[] ───────────────────────────────────────────────
    if (Array.isArray(facets) && facets.length > 0) {
        return { facets, configOverrides: {} };
    }

    return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// resolvePageConfig — CASCADE 3 niveaux
// ═══════════════════════════════════════════════════════════════════════════

const resolvePageConfig = (pageSettings) => {
    const result = _extractFromSettings(pageSettings);
    if (result) return result;

    return {
        facets:          DEFAULT_FACETS,
        configOverrides: DEFAULT_CONFIG_OVERRIDES,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT
// ═══════════════════════════════════════════════════════════════════════════

const NewProducts = (props) => {
    const {
        infoSource,
        laguangeElakay,
        homeStyles,
    } = props;

    const token = infoSource?.token || null;

    // ─── Filtres ──────────────────────────────────────────────────────────────
    const [myFilters, setMyFilters] = useState({ filters: {} });

    const handlePrice = useCallback((value) => {
        let array = [];
        for (let key in prices) {
            if (prices[key]._id === parseInt(value)) {
                array = prices[key].array;
            }
        }
        return array;
    }, []);

    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => {
            const newFilters   = { ...prev };
            newFilters.filters = { ...prev.filters };

            if (filterBy === 'price') {
                newFilters.filters[filterBy] = handlePrice(filters);
            } else {
                newFilters.filters[filterBy] = filters;
            }

            return newFilters;
        });
    }, [handlePrice]);

    // ─── CASCADE ──────────────────────────────────────────────────────────────
    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];

    const { facets, configOverrides } = useMemo(
        () => resolvePageConfig(pageSettings),
        [
            JSON.stringify(pageSettings?.sections),
            JSON.stringify(pageSettings?.facets),
        ]
    );

    // ─── DataLoader — sections standard uniquement ────────────────────────────
    //
    // IMPORTANT: productGrid est une section INDÉPENDANTE
    // → Son DataLoader est géré par SectionDataLoader dans DynamicPageRenderer
    // → Ce DataLoader global charge UNIQUEMENT les sections standard:
    //   userView, followMe, bestSellers, dealTodays, ...
    //
    // Les facets INDEPENDENT_SECTION_IDS sont filtrés automatiquement
    // car SectionDataLoader les charge séparément

    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        filters: myFilters.filters,
    });

    // ─── Sync infoSource → pageData (sections loaderType NONE) ───────────────
    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) {
            mergePageData(updates);
        }
    }, [infoSource, facets]);

    // ─── Recharger quand facets / filtres changent ────────────────────────────
    useEffect(() => {
        loadPageData();
    }, [JSON.stringify(facets), JSON.stringify(myFilters.filters)]);

    // ─── P1 flag ──────────────────────────────────────────────────────────────
    const P1 = (pageData?.size || 0) > 0;

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Layout
            infoSource={infoSource}
            loading={loading}
            SliderPunShow={false}
            prices={prices}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            P1={P1}
            {...infoSource?.allSettings?.layout}
        >
            <div style={infoSource.styleNav}>
                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    // ProductGridPage is self-loading. Passing the logical
                    // page id makes its request use the SAME `newProducts`
                    // facet as Home's slider instead of silently falling
                    // back to the generic catalog (`page="home"`).
                    page={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    reloadFacets={reloadFacets}
                    configOverrides={configOverrides}
                    // ── Pour sections indépendantes (productGrid) ────────────
                    myFilters={myFilters}
                />

                {loading && !pageData?.productsByArrival?.length && (
                    <>
                        <LoadingSwiper myStyles={homeStyles} />
                        <LoadingSwiper myStyles={homeStyles} />
                    </>
                )}
            </div>
        </Layout>
    );
};

export default NewProducts;
